import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { getCronosHeader } from 'core/utils/cronosHeader';
import { SuggestionResult } from 'core/type/SuggestionResult.types';
import { getBootstrapConfig } from 'core/window/windowService';
import { SeoModel } from 'core/type/Seo';
import { TopDestination, TopDestinationsGroup, TopDestinationType } from 'core/type/ActivitiesTopDestinations.types';
import { ReviewTranslationMessage, TranslationResult } from 'core/type/TranslationResult.types';
import { NearBySuggestion } from 'core/type/PlaceSuggestion';
import { extraOptions } from 'store/api/ApiConstants';
import { logError } from 'core-framework/util/LogMessage';
import { LandingLink } from 'core/type/LandingLink.types';
import { measureLandingLinkApiResponse } from 'store/api/measurements/landingLinkApiMeasurement';
import { fetchResponse } from 'store/api/apiUtils';

const TOP_DESTINATION_LIMIT = 20;

// Exported for testing
export const createGetSuggestResultQueryArgs = (queryArgs: string) => {
    const config = getBootstrapConfig();
    const url = `${config.rootUrl}/api/cronos/GetResultSuggestion/${config.language.languageId}?searchText=${queryArgs}`;
    const headers = getCronosHeader();
    return { url, headers, method: 'GET' };
};

// Exported for testing
export const createSeoQueryArgs = () => {
    const config = getBootstrapConfig();
    const url = '/api/cronos/seo/base';
    const headers = getCronosHeader();
    return { url, headers, method: 'POST', body: { pageTypeId: config.pageConfig.pageTypeId, objectId: 0 } };
};

// Exported for testing
export const createTopDestinationArgs = () => {
    const config = getBootstrapConfig();
    const endpoint = `${config.rootUrl}/api/cronos/GetTopDestinations`;
    const params = `?origin=${config.pageConfig.origin}&languageId=${config.language.languageId}&cultureCode=${config.language.cultureCode}&limit=${TOP_DESTINATION_LIMIT}&requestType=${TopDestinationType.Domestic}`;
    const url = endpoint + params;
    const headers = getCronosHeader();
    return { url, headers, method: 'GET' };
};

export const createTopCitiesArgs = (query: { isMobileScreen: boolean; isHomePage: boolean }) => {
    const { isMobileScreen, isHomePage } = query;
    const config = getBootstrapConfig();
    const endpoint = `${config.rootUrl}/api/cronos/GetTopCities`;
    const imageLimit = isMobileScreen ? 5 : 10;
    const origin = isHomePage ? config.pageConfig.origin : config.geo?.countryISO2.toUpperCase();
    const params = `?origin=${origin}&languageId=${config.language.languageId}&cultureCode=${config.language.cultureCode}&limit=${imageLimit}`;
    const url = endpoint + params;
    const headers = getCronosHeader();
    return { url, headers, method: 'GET' };
};

export const createGetCityByGeoLocationArgs = (query: { latitude: number; longitude: number }) => {
    const config = getBootstrapConfig();
    const endpoint = `${config.rootUrl}/api/cronos/GetCityByGeoLocation`;
    const origin = config.pageConfig.origin;
    const params = `?origin=${origin}&languageId=${config.language.languageId}&latitude=${query.latitude}&longitude=${query.longitude}`;
    const url = endpoint + params;
    const headers = getCronosHeader();
    return { url, headers, method: 'GET' };
};

export const createTranslationQueryArgs = (review: ReviewTranslationMessage) => {
    const config = getBootstrapConfig();
    const url = `${config.rootUrl}/api/cronos/GetTranslation`;
    const headers = getCronosHeader();
    const body = {
        title: review.title,
        description: review.description,
        reply: review.reply,
        languageId: config.language.languageId,
    };
    return { url, headers, body, method: 'POST' };
};

/**
 * API for calling Activity Cronos C# backend
 */
export const activitiesApi = createApi({
    reducerPath: 'api',
    baseQuery: retry(fetchBaseQuery(), { maxRetries: 2 }),
    keepUnusedDataFor: 0,
    refetchOnFocus: false,
    refetchOnMountOrArgChange: false,
    refetchOnReconnect: true,

    serializeQueryArgs: (args) => {
        return args.endpointName;
    },
    endpoints: (builder) => ({
        getSuggestResult: builder.query<SuggestionResult[], string>({
            extraOptions,
            keepUnusedDataFor: 60 * 5, // Cache data for 5 minutes
            query: createGetSuggestResultQueryArgs,
        }),
        getSeoResult: builder.query<SeoModel, {}>({
            extraOptions,
            query: createSeoQueryArgs,
        }),
        getTopDestinationResult: builder.query<TopDestinationsGroup[], {}>({
            extraOptions,
            query: createTopDestinationArgs,
        }),
        getTranslationResult: builder.query<TranslationResult, ReviewTranslationMessage>({
            extraOptions,
            query: createTranslationQueryArgs,
        }),
        getTopCitiesResult: builder.query<TopDestination[], {}>({
            extraOptions,
            query: createTopCitiesArgs,
        }),
        getCityByGeoRadius: builder.query<NearBySuggestion, {}>({
            extraOptions,
            query: createGetCityByGeoLocationArgs,
        }),
    }),
});

export const LandingLinkProviderApi = {
    generateShortenUrl: async (url: string): Promise<string> => {
        const config = getBootstrapConfig();
        const encodedUrl = encodeURIComponent(url);
        try {
            const response = await fetchResponse(`${config.rootUrl}/api/cronos/shortenUrl?url=${encodedUrl}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = response.body as unknown as LandingLink;
            measureLandingLinkApiResponse(data, config.pageConfig.pageTypeId, config);
            return data.shortenUrl;
        } catch (e) {
            measureLandingLinkApiResponse({ shortenUrl: '', status: 'error' }, config.pageConfig.pageTypeId, config);
            logError('[GenerateShortenUrl] Error fetching shorten URL', config.pageConfig.pageTypeId);
            return '';
        }
    },
};

export const {
    useLazyGetSuggestResultQuery,
    useLazyGetSeoResultQuery,
    useLazyGetTopDestinationResultQuery,
    useLazyGetTranslationResultQuery,
    useLazyGetTopCitiesResultQuery,
    useLazyGetCityByGeoRadiusQuery,
} = activitiesApi;
export const useSuggestResultQueryState = activitiesApi.endpoints.getSuggestResult.useQueryState;
export default activitiesApi.reducer;
